// Values mirror the server enums ReportingFollowUpSection, FollowUpPaymentMethod and FollowUpStatus.

/** Fired on window after the follow-up modal saved; detail: { caseId }. Lets the client page refresh. */
export const REPORTING_FOLLOW_UP_SAVED_EVENT = 'reportingFollowUp:saved';

export const FOLLOW_UP_SECTIONS = {
    VAT: 1,
    INCOME_TAX_ADVANCE: 2,
    INCOME_TAX_WITHHOLDING: 3,
    PERSONAL_NATIONAL_INSURANCE: 4,
    NATIONAL_INSURANCE_WITHHOLDING: 5,
};

export const PAYMENT_METHOD_OPTIONS = [
    { value: 0, labelKey: 'FollowUpPaymentNotDefined' },
    { value: 1, labelKey: 'FollowUpPaymentDirectDebit' },
    { value: 2, labelKey: 'FollowUpPaymentCreditCard' },
    { value: 3, labelKey: 'FollowUpPaymentCheck' },
    { value: 4, labelKey: 'FollowUpPaymentCash' },
    { value: 5, labelKey: 'FollowUpPaymentIndependently' },
];

export const FOLLOW_UP_STATUS_OPTIONS = [
    { value: 0, labelKey: 'FollowUpStatusNotDefined' },
    { value: 1, labelKey: 'FollowUpStatusMaterialReceived' },
    { value: 2, labelKey: 'FollowUpStatusMaterialFiled' },
    { value: 3, labelKey: 'FollowUpStatusReceivedWaitingForClosing' },
    { value: 4, labelKey: 'FollowUpStatusReadyForReporting' },
    { value: 5, labelKey: 'FollowUpStatusReportedWaitingForPayment' },
    { value: 6, labelKey: 'FollowUpStatusSentForPayment' },
    { value: 7, labelKey: 'FollowUpStatusReturnedFromMail' },
];

/** Keys of the manually entered amounts each section stores in ManualValues. */
export const MANUAL_KEYS = {
    ADVANCE: {
        excessPaymentsBooks: 'excessPaymentsBooks',
        excessPaymentsReport: 'excessPaymentsReport',
        // ReportedAdvance keeps only the offset withholding, so the reported (uncapped) amount lives here.
        reportedWithholding: 'reportedWithholding',
    },
    WITHHOLDING: {
        reportRecipients: 'reportRecipients',
        reportTotalPayments: 'reportTotalPayments',
        reportTotalWithholding: 'reportTotalWithholding',
        reportTotalSalaries: 'reportTotalSalaries',
        reportSalaryWithholding: 'reportSalaryWithholding',
    },
    PERSONAL_NI: {
        ownerAssessmentPayment: 'ownerAssessmentPayment',
        ownerCurrentPayment: 'ownerCurrentPayment',
        ownerInsuredIncome: 'ownerInsuredIncome',
        spouseAssessmentPayment: 'spouseAssessmentPayment',
        spouseCurrentPayment: 'spouseCurrentPayment',
        spouseInsuredIncome: 'spouseInsuredIncome',
    },
    NI_WITHHOLDING: {
        numberOfEmployees: 'numberOfEmployees',
        amountToPay: 'amountToPay',
    },
};

export const MONTHLY_REPORTING = 'חודשי';
export const BI_MONTHLY_REPORTING = 'דו חודשי';

export const isBiMonthlyReporting = (reportingType) =>
    (reportingType || '').trim() === BI_MONTHLY_REPORTING;

/** Display label of a stored reporting type; empty when the client has none set. */
export const reportingTypeLabel = (reportingType, t) => {
    const value = (reportingType || '').trim();
    if (!value) return '';
    if (value === BI_MONTHLY_REPORTING) return t('FollowUpBiMonthly');
    if (value === MONTHLY_REPORTING) return t('FollowUpMonthly');
    return value;
};

/** Reporting type that drives a section's period, same rules as the server's GetReportingType. */
export const getSectionReportingType = (client, section) => {
    if (!client) return null;
    switch (section) {
        case FOLLOW_UP_SECTIONS.VAT:
            return client.reportingType;
        case FOLLOW_UP_SECTIONS.INCOME_TAX_ADVANCE:
        case FOLLOW_UP_SECTIONS.INCOME_TAX_WITHHOLDING:
            return client.advancedReportingType ?? client.reportingType;
        default:
            return null;
    }
};

/**
 * Period of a section for the chosen report month (1-12). Bi-monthly periods start on an odd month,
 * matching the server. Dates are plain `YYYY-MM-DD` strings so they never shift across time zones.
 */
export const getSectionPeriod = (reportingType, year, month) => {
    const isBiMonthly = isBiMonthlyReporting(reportingType);
    const startMonth = isBiMonthly && month % 2 === 0 ? month - 1 : month;
    const endMonth = isBiMonthly ? startMonth + 1 : startMonth;
    const lastDay = new Date(year, endMonth, 0).getDate();
    return {
        isBiMonthly,
        startMonth,
        endMonth,
        startDate: toDateString(year, startMonth, 1),
        endDate: toDateString(year, endMonth, lastDay),
    };
};

export const toDateString = (year, month, day) =>
    `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

/** Server DateTime (no time zone) → `YYYY-MM-DD` for date inputs. */
export const toDateInputValue = (value) => {
    if (!value) return '';
    const match = String(value).match(/^(\d{4}-\d{2}-\d{2})/);
    return match ? match[1] : '';
};

export const todayDateString = () => {
    const now = new Date();
    return toDateString(now.getFullYear(), now.getMonth() + 1, now.getDate());
};

/** `YYYY-MM-DD` → local Date (not `new Date(str)`, which parses as UTC midnight). */
export const dateStringToDate = (value) => {
    const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
    return match ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])) : null;
};

export const dateToDateString = (date) =>
    toDateString(date.getFullYear(), date.getMonth() + 1, date.getDate());

export const getMonthName = (month, language) =>
    new Date(2000, month - 1, 1).toLocaleString(language || 'he', { month: 'long' });

export const formatPeriodLabel = (period, language) => {
    if (!period) return '';
    const start = getMonthName(period.startMonth, language);
    if (!period.isBiMonthly) return start;
    return `${start}-${getMonthName(period.endMonth, language)}`;
};

export const toNumber = (value) => {
    if (value === null || value === undefined || value === '') return 0;
    const parsed = Number(String(value).replace(/,/g, ''));
    return Number.isFinite(parsed) ? parsed : 0;
};

export const isBlankValue = (value) => value === '' || value === null || value === undefined;

/** A reported value that is filled in and differs from the books; amounts compare in whole shekels. */
export const reportDiffers = (books, report, { exact = false } = {}) => {
    if (isBlankValue(report) || books === null || books === undefined) return false;
    if (exact) return Math.abs(toNumber(books) - toNumber(report)) > 0.0001;
    return Math.round(toNumber(books)) !== Math.round(toNumber(report));
};
